import {
  type SchoolNcesId,
  schoolNcesIdSchema,
} from "@nimble/candidate-api/types";
import { UserId } from "@nimble/datamodel/types/userId";
import { useQuery } from "@tanstack/react-query";
import { create, windowScheduler } from "@yornaath/batshit";

import { existsSavedSchools } from "@/lib/api-clients/candidate-api-client";

export function getSavedSchoolQueryKey(userId: UserId, ncesId: string) {
  return ["saved-school", userId, ncesId] as const;
}

type SavedSchoolRequest = {
  userId: UserId;
  ncesId: SchoolNcesId;
};

const batchedFetcher = create({
  fetcher: async (items: SavedSchoolRequest[]) => {
    const firstItem = items[0];

    if (!firstItem) return {};

    const { userId } = firstItem;
    const ncesIds = items.map((item) => item.ncesId);
    const { data } = await existsSavedSchools(userId, ncesIds);
    return data;
  },
  resolver: (data, item) => data[item.ncesId] ?? false,
  scheduler: windowScheduler(10),
});

export default function useSavedSchool(userId: UserId, ncesId: string) {
  return useQuery({
    queryKey: getSavedSchoolQueryKey(userId, ncesId),
    queryFn: () => {
      const schoolNcesId = schoolNcesIdSchema.safeParse(ncesId);

      if (!schoolNcesId.success) {
        return false;
      }

      return batchedFetcher.fetch({ userId, ncesId: schoolNcesId.data });
    },
    retry: false,
  });
}
